import { useState } from 'react';
import { useSnapshot } from 'valtio';

import { cx } from '@libs/classnames';
import { useClickOutside } from '@libs/hooks/useClickOutside';
import { useOnEscape } from '@libs/hooks/useOnKeyDown';

import {
  Button,
  Label,
  Radio,
  Slider,
  Toggle,
  PositionEditor,
  PositionsCatalog,
  TimeControl,
} from '@features/game/ui/PopupControls';
import stylesControls from '@features/game/ui/PopupControls/PopupControls.module.css';

import { CATALOG_ELO } from '../../contrants';
import { IGameSettings } from '../../types';
import { ComputerSettingsStore } from './ComputerSettings.model';

import styles from './ComputerSettings.module.css';

/*
    ____,-------------------------------,____
    \   |           Component           |   /
    /___|-------------------------------|___\
*/

const COLORS = [
  { title: 'Белые', value: 'white' as const },
  { title: 'Чередовать', value: 'alternate' as const },
  { title: 'Черные', value: 'black' as const },
];

const POSITIONS = [
  { title: 'Расставить вручную', value: 'manual' as const },
  { title: 'Каталог позиций', value: 'catalog' as const },
];

const ELOS = [
  0, 900, 1000, 1100, 1200, 1300, 1400, 1500, 1600, 1700, 1800, 1900, 2000, 2100, 2200, 2300, 2400,
  2500,
];

type Props = {
  className?: string;
  initialSettings?: IGameSettings;
  isMobile: boolean;
  onPlay?: (cfg: IGameSettings) => void;
  onClose?: () => void;
  onCancel?: () => void;
};

export function ComputerSettings({
  className,
  initialSettings,
  isMobile,
  onPlay,
  onClose,
  onCancel,
}: Props) {
  const [store] = useState(() => new ComputerSettingsStore(initialSettings, onPlay));
  store.keepUpToDate(onPlay);
  const snapshot = useSnapshot(store.state) as typeof store.state;
  const isCatalog = snapshot.positionsTab === 'catalog';

  const ref = useClickOutside(onClose);
  useOnEscape(onClose);

  return (
    <article
      ref={ref}
      className={cx(styles.settings, stylesControls.container, className, {
        [stylesControls.is_mobile]: isMobile,
        [styles.is_mobile]: isMobile,
      })}
    >
      <section className={styles.basic_settings_grid}>
        <div>
          <Label>Рейтинг ИИ</Label>
          <Slider
            value={isCatalog ? CATALOG_ELO : snapshot.elo}
            disabled={isCatalog}
            options={ELOS}
            onChange={store.onEloChange}
          />
        </div>

        <div>
          <Label>Мои фигуры</Label>
          <Radio
            options={COLORS}
            selected={isCatalog ? snapshot.positionsSelectedColor : snapshot.color}
            disabled={isCatalog}
            onChange={store.onColorChange}
          />
        </div>

        <div>
          <Label>Время белых</Label>
          <TimeControl value={snapshot.white} onChange={store.onWhiteTimeControl} />
        </div>

        <div>
          <Label>Время черных</Label>
          <TimeControl value={snapshot.black} onChange={store.onBlackTimeControl} />
        </div>
      </section>

      {isMobile && (
        <Toggle
          big
          value={snapshot.displayPositionsOnMobile}
          onChange={store.onDisplayPositionsChange}
        >
          Расставить фигуры
        </Toggle>
      )}
      {(snapshot.displayPositionsOnMobile || !isMobile) && (
        <>
          <Radio
            options={POSITIONS}
            selected={snapshot.positionsTab}
            onChange={store.onPositionsTab}
          />
          {snapshot.positionsTab === 'manual' && (
            <PositionEditor fen={snapshot.fen} isMobile={isMobile} onChange={store.onFenChange} />
          )}
          {snapshot.positionsTab === 'catalog' && (
            <PositionsCatalog
              catalog={snapshot.positionsCatalog}
              selected={snapshot.positionsSelected}
              onChange={store.onPositionChange}
            />
          )}
        </>
      )}

      <div className={styles.actions}>
        <Button big onClick={store.onPlay} disabled={!snapshot.canPlay}>
          Играть
        </Button>
        <Button ghost big onClick={onCancel}>
          Отмена
        </Button>
      </div>
    </article>
  );
}
